import './timeregistration-user-registrations.css';
import List from '../../list/list';
import Avatar from '../../avatar/avatar';
import { isDateLocked } from '../../../utils/isDateLocked';
import { getCompanyStartDate } from '../../../utils/getCompanyStartDate';
import TimeRegistrationWorkBar from '../work-bar/timeregistration-work-bar';
import { convertValueToHoursAndMinutes } from '../../../utils/convertValueToHoursAndMinutes';
import { useContext, useMemo } from 'react';
import DANISH_HOLIDAYS from '../../../constants/time/danishHolidays';
import { convertTimeOffTypeUUIDToName } from '../../../views/timeregistration/timeregistration-utils';
import Icon from '../../icon/icon';
import { UserInfoContext } from '../../../context';

function TimeRegistrationUserRegistrations({
  employees,
  metrics,
  setModalEmployeeId,
  setModalDate,
  setIsModalOpen,
  weekMode,
}) {
  const userInfo = useContext(UserInfoContext);

  const items = useMemo(() => {
    const timeOffTypes = userInfo?.companyData?.timeOffTypes || [];
    const processedDates = new Set();
    return Object.keys(metrics)
      .flatMap((date) => {
        let dateToDisplay;
        let isHoliday = null;
        if (weekMode) {
          const match = date.match(/(\d{4})-U(\d{1,2})/);
          if (match) {
            const year = match[1];
            const week = match[2];
            dateToDisplay = `Uge ${week} (${year})`;
          } else {
            dateToDisplay = date;
          }
        } else {
          // Defensive: only treat as date if matches YYYY-MM-DD
          if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            dateToDisplay = new Date(date).toLocaleDateString('da-DK', {
              weekday: 'long',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            });
            isHoliday = DANISH_HOLIDAYS.filter(
              (holiday) =>
                holiday.value === new Date(date).toISOString().split('T')[0]
            )[0];
          } else {
            // Fallback for invalid date keys
            dateToDisplay = date;
            isHoliday = null;
          }
        }
        return Object.keys(metrics[date])
          .flatMap((employeeId) => {
            const employee = employees.find(
              (employee) => employee.id === employeeId
            );
            if (!employee) {
              return null;
            }
            if (employee?.data?.settings?.timeRegistration?.exempt) {
              return null;
            }
            const employeeMetrics = metrics[date][employeeId];
            const allMetricsZero =
              employeeMetrics.totalWorkTime === 0 &&
              employeeMetrics.normTime === 0 &&
              employeeMetrics.overtime.hours === 0 &&
              employeeMetrics.sickLeave.hours === 0 &&
              employeeMetrics.timeOff.hours === 0 &&
              employeeMetrics.holidays.hours === 0;
            if (allMetricsZero) {
              return null;
            }
            const firstInDate = !processedDates.has(date);
            if (firstInDate) {
              processedDates.add(date);
            }

            // Determine if this row is week mode and calculate locked status for the row
            const weekMatchRow = date.match(/(\d{4})-U(\d{1,2})/);
            const isWeekModeRow = !!weekMatchRow;
            const locked = isWeekModeRow
              ? isDateLocked(
                  (() => {
                    const year = weekMatchRow[1];
                    const week = weekMatchRow[2];
                    const monday = new Date(
                      Number(year),
                      0,
                      1 + (Number(week) - 1) * 7
                    );
                    const dow = monday.getDay();
                    if (dow <= 4) {
                      monday.setDate(monday.getDate() - monday.getDay() + 1);
                    } else {
                      monday.setDate(monday.getDate() + 8 - monday.getDay());
                    }
                    return monday;
                  })(),
                  employee?.data?.timeLogApprovedWeeks || [],
                  employee?.data?.startDate,
                  getCompanyStartDate(userInfo.companyData)
                )
              : isDateLocked(
                  new Date(date),
                  employee?.data?.timeLogApprovedWeeks || [],
                  employee?.data?.startDate,
                  getCompanyStartDate(userInfo.companyData)
                );

            return {
              id: employee.id,
              className: firstInDate
                ? 'timeregistration-user-registrations-row-first-in-date'
                : '',
              onClick: () => {
                setModalEmployeeId(employee.id);
                setModalDate(date);
                setIsModalOpen((prev) => !prev);
              },
              fields: [
                {
                  flex: 15,
                  label: 'Medarbejder',
                  value: (
                    <div
                      className={`timeregistration-user-registrations-row-profile ${
                        firstInDate ? 'first-in-date' : ''
                      } ${
                        employee.data.employmentStatus === 'inactive'
                          ? 'timeregistration-user-registrations-row-profile--inactive'
                          : ''
                      }`}
                      {...(firstInDate
                        ? {
                            'data-date': weekMode
                              ? dateToDisplay
                              : `${dateToDisplay}${
                                  isHoliday ? ' - ' + isHoliday.label : ''
                                }`,
                          }
                        : {})}
                    >
                      <Avatar
                        firstName={employee.data.firstName}
                        lastName={employee.data.lastName}
                        profilePhotoUrl={employee.data.profilePhotoUrl}
                        size="small"
                      />
                      <div className="employees-list-row-name-wrapper">
                        <span className="employees-list-row-name">
                          {employee.data.firstName} {employee.data.lastName}
                        </span>
                      </div>
                    </div>
                  ),
                  sortBy: `${employee.data.firstName} ${employee.data.lastName}`,
                  searchBy: `${employee.data.firstName} ${employee.data.lastName}`,
                },
                {
                  label: 'Arbejdstid',
                  flex: 15,
                  value: (
                    <TimeRegistrationWorkBar
                      hours={employeeMetrics?.totalWorkTime || 0}
                      total={employeeMetrics?.normTime || 0}
                    />
                  ),
                  sortBy: employeeMetrics?.totalWorkTime || 0,
                  align: 'right',
                },
                {
                  label: 'Sygdom',
                  flex: 8,
                  value: (
                    <div className="timeregistration-user-registrations-row-default">
                      {!weekMode && employeeMetrics?.sickLeave.hasNote ? (
                        <Icon name="comment" />
                      ) : null}
                      {convertValueToHoursAndMinutes(
                        employeeMetrics?.sickLeave.hours || 0
                      )}
                    </div>
                  ),
                  sortBy: employeeMetrics?.sickLeave.hours || 0,
                  align: 'right',
                },
                {
                  label: 'Fri',
                  flex: 8,
                  value: (
                    <div className="timeregistration-user-registrations-row-default">
                      {!weekMode && employeeMetrics?.timeOff?.hasNote ? (
                        <Icon name="comment" />
                      ) : null}
                      {convertValueToHoursAndMinutes(
                        employeeMetrics?.timeOff.hours || 0
                      )}
                    </div>
                  ),
                  sortBy: employeeMetrics?.timeOff.hours || 0,
                  align: 'right',
                },
                ...(!weekMode
                  ? [
                      {
                        label: 'Fri type',
                        flex: 10,
                        value: timeOffTypes && timeOffTypes.length > 0 && (
                          <div className="timeregistration-user-registrations-row-timeoff-type">
                            {convertTimeOffTypeUUIDToName(
                              employeeMetrics?.timeOff?.timeOffType,
                              timeOffTypes
                            ) || '-'}
                          </div>
                        ),
                        sortBy: employeeMetrics?.timeOff.hours || 0,
                        align: 'right',
                      },
                    ]
                  : []),
                {
                  label: 'Ferie',
                  flex: 8,
                  value: (
                    <div className="timeregistration-user-registrations-row-default">
                      {!weekMode && employeeMetrics?.holidays?.hasNote ? (
                        <Icon name="comment" />
                      ) : null}
                      {convertValueToHoursAndMinutes(
                        employeeMetrics?.holidays.hours || 0
                      )}
                    </div>
                  ),
                  sortBy: employeeMetrics?.holidays.hours || 0,
                  align: 'right',
                },
                {
                  label: 'U.f. normtid',
                  flex: 8,
                  value: (
                    <div className="timeregistration-user-registrations-row-default">
                      {!weekMode && employeeMetrics?.overtime?.hasNote ? (
                        <Icon name="comment" />
                      ) : null}
                      {convertValueToHoursAndMinutes(
                        employeeMetrics?.overtime.hours || 0
                      )}
                    </div>
                  ),
                  sortBy: employeeMetrics?.overtime.hours || 0,
                  align: 'right',
                },
                // If in week mode, add a locked/unlocked column
                ...(weekMode
                  ? [
                      {
                        label: 'Godkendt',
                        flex: 10,
                        value: (
                          <span
                            className={
                              locked
                                ? 'timeregistration-user-registrations-row-locked'
                                : 'timeregistration-user-registrations-row-unlocked'
                            }
                          >
                            {locked ? 'Godkendt' : 'Ikke godkendt'}
                          </span>
                        ),
                        sortBy: employeeMetrics?.locked ? 1 : 0,
                        align: 'right',
                      },
                    ]
                  : []),
              ],
            };
          })
          .filter((item) => item);
      })
      .filter((item) => item);
  }, [
    employees,
    metrics,
    setIsModalOpen,
    setModalDate,
    setModalEmployeeId,
    userInfo.companyData,
    weekMode,
  ]);

  return (
    <div className="timeregistration-user-registrations">
      <List
        hideListActions
        head={[
          { label: 'Medarbejder', sortable: false, flex: 15 },
          { label: 'Arbejdstid', sortable: false, flex: 15, align: 'right' },
          { label: 'Sygdom', sortable: false, flex: 8, align: 'right' },
          { label: 'Fri', sortable: false, flex: 8, align: 'right' },
          ...(!weekMode
            ? [
                {
                  label: 'Fri type',
                  sortable: false,
                  flex: 10,
                  align: 'right',
                },
              ]
            : []),
          { label: 'Ferie', sortable: false, flex: 8, align: 'right' },
          { label: 'U.f. normtid', sortable: false, flex: 8, align: 'right' },
          ...(weekMode
            ? [{ label: 'Godkendt', sortable: false, flex: 10, align: 'right' }]
            : []),
        ]}
        items={items}
      />
    </div>
  );
}

export default TimeRegistrationUserRegistrations;
